import { getScales } from "@/components/PddContextProvider/DiceCanvases/helpers/getScales";
import {
  DiceActionTypes,
  DiceRollActionTypes,
  getDiceServiceUrl,
  getMediaUrl,
  isDebug,
  PddPayloadTypes,
} from "@/constants";
import { Context } from "@/context";
import { playSound } from "@/helpers/playSound";
import { preloadAssets } from "@/helpers/preloadAssets";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import { key } from "@dndbeyond/message-broker-lib/dist/lib/constants";
import { isEmpty } from "lodash";
import { useContext, useEffect, useRef } from "react";

export const useRenderedListeners = () => {
  const {
    nodeEnv,
    env,
    user,
    renderedWorker,
    physicsWorker,
    setIsRenderedWorkerReady,
    setHasRenderedWorkerErrored,
    logger,
    errorCallback,
    toastCallback,
    userManifest,
    diceSets,
  } = useContext(Context);
  const diceServiceUrl = getDiceServiceUrl(String(nodeEnv), String(env));
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const mediaUrl = getMediaUrl(String(nodeEnv), String(env));
  const settings = useUserDiceDataStore(
    (state) => state.getUserDiceData(Number(user?.id))?.settings,
  );
  const volume = settings?.volume !== undefined ? settings?.volume : 1;
  const shadowQuality = settings?.shadowQuality || 3;
  const activeSources = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (
      isEmpty(userManifest) ||
      isEmpty(diceSets) ||
      !physicsWorker ||
      !renderedWorker
    )
      return;

    if (isDebug) {
      console.log("Setting up renderedWorker listeners");
    }

    const handleRenderedMessage = ({
      data: { type, payload },
    }: MessageEvent) => {
      if (isDebug && type !== PddPayloadTypes.WorkerStats) {
        console.log(
          "[renderedWorkerListeners] Message from renderedWorker:",
          type,
          payload,
        );
      }

      if (type === PddPayloadTypes.ComponentMounted) {
        renderedWorker.postMessage({
          type: PddPayloadTypes.Manifests,
          payload: userManifest,
        });

        if (isDebug) {
          console.log(
            "[renderedWorkerListeners] Sent userManifest to renderedWorker:",
            userManifest,
          );
        }

        const userSettings = {
          ...settings,
          shadowQuality,
          volume,
        };

        renderedWorker.postMessage({
          type: PddPayloadTypes.UserSettings,
          payload: userSettings,
        });

        if (isDebug) {
          console.log(
            "[renderedWorkerListeners] Sent user settings to renderedWorker:",
            userSettings,
          );
        }

        renderedWorker.postMessage({
          type: PddPayloadTypes.DiceSets,
          payload: diceSets,
        });

        if (isDebug) {
          console.log(
            "[renderedWorkerListeners] Sent dice sets to renderedWorker:",
            diceSets,
          );
        }

        setIsRenderedWorkerReady?.(true);

        if (isDebug) {
          console.log("renderedWorker is ready");
        }
      } else if (type === DiceRollActionTypes.Fulfilled) {
        window[key]?.dispatch(payload);
      } else if (type === PddPayloadTypes.PlaySound) {
        if (!payload.isCollision && activeSources.current.has(payload.url))
          return;

        activeSources.current.add(payload.url);
        playSound(
          payload.url,
          payload.volume * (volume ?? 1),
          activeSources.current,
        );
      } else if (type === PddPayloadTypes.RemoveRoll) {
        physicsWorker.postMessage({
          type: PddPayloadTypes.RemoveRoll,
          payload,
        });
      } else if (type === DiceActionTypes.SetUpdate) {
        const scales = getScales({ [payload.setId]: payload.manifest });

        if (!isEmpty(scales)) {
          physicsWorker.postMessage({
            type: PddPayloadTypes.Scales,
            payload: scales,
          });
        }

        preloadAssets(mediaUrl, [payload.manifest], customUrlMap);
      } else if (type === PddPayloadTypes.ToastMessage) {
        toastCallback?.(payload);
      } else if (type === PddPayloadTypes.PreRoll) {
        const scales = getScales({
          [payload.message.data.setId]: payload.manifest,
        });

        if (!isEmpty(scales)) {
          if (isDebug) {
            console.log(
              "[renderedWorkerListeners] Sending scales to physicsWorker before starting roll",
              scales,
            );
          }

          physicsWorker.postMessage({
            type: PddPayloadTypes.Scales,
            payload: scales,
          });
        }

        if (!payload.preloaded) {
          preloadAssets(mediaUrl, [payload.manifest], customUrlMap);
        }

        if (isDebug) {
          console.log(
            "[renderedWorkerListeners] Starting roll:",
            payload.message,
          );
        }

        physicsWorker.postMessage({
          type: PddPayloadTypes.StartRoll,
          payload: payload.message,
        });
      }
    };
    const handleError = (error: ErrorEvent) => {
      // clean up message handler if error happens before component mounts
      renderedWorker.removeEventListener("message", handleRenderedMessage);

      logger?.error("renderedWorker error", {
        exception: JSON.stringify(error.message),
      });

      renderedWorker.postMessage({
        type: PddPayloadTypes.RemoveEventListener,
      });
      errorCallback?.(
        "renderedWorker encountered an error. Falling back to instant dice.",
      );
      setHasRenderedWorkerErrored?.(true);
    };

    renderedWorker.addEventListener("error", handleError);
    renderedWorker.addEventListener("message", handleRenderedMessage);

    return () => {
      renderedWorker.removeEventListener("error", handleError);
      renderedWorker.removeEventListener("message", handleRenderedMessage);
    };
  }, [
    customUrlMap,
    diceServiceUrl,
    diceSets,
    errorCallback,
    logger,
    userManifest,
    mediaUrl,
    physicsWorker,
    renderedWorker,
    setHasRenderedWorkerErrored,
    setIsRenderedWorkerReady,
    shadowQuality,
    toastCallback,
    volume,
    settings,
  ]);
};
